import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useIntersection, useReducedMotion } from '@mantine/hooks'
import {
  IconBolt,
  IconBookmark,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCurrencyDollar,
  IconPackage,
  IconShare,
  IconSparkles,
  IconTool,
} from '@tabler/icons-react'
import { AnimatedValue, getAnimatedValueTransitionEndMs } from './AnimatedValue'
import { AnonymousRefinementBar } from './AnonymousRefinementBar'
import { SparkleIndicator } from './SparkleIndicator'
import { EstimateComparison } from './EstimateComparison'
import { PERSONALIZED_FOR_YOUR, PersonalizedBadge } from './PersonalizedBadge'
import type { CustomizationSummary } from '../types'
import { diyDifficultyBadgeColor } from '@/styles/theme/tokens'

const DIFFICULTY_ICONS: Record<string, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
}

interface Tool {
  name: string
  required: boolean
}

interface Material {
  name: string
  cost?: number
  quantity?: number
  unit?: string
}

interface ProjectGuideHeaderProps {
  title: string
  difficulty: string
  estimatedHours?: number
  estimatedCostMin?: number
  estimatedCostMax?: number
  tools?: Array<Tool>
  materials?: Array<Material>
  isCustomized?: boolean
  /** When personalized without chip labels, fallback to this one-line summary */
  customizationSummary?: CustomizationSummary
  /** Labels for all answered Q&amp;A, template order, for the personalized badge + chips */
  answerChipLabels?: Array<string>
  onEditCustomization?: () => void
  /** Stable fingerprint of Q&A answers — when it changes, stat sparkles reset then re-derive from new values */
  answersFingerprint?: string
  /** Q&amp;A usage tracker (inline row): anonymous = phase copy, signed-in = running total */
  anonymousRefinement?: {
    show: true
    count: number
    display: 'capped' | 'total'
    accountMode: 'anon' | 'signedIn'
    onSaveProgress?: () => void
    onSignUp?: () => void
    onSignIn?: () => void
  }
  /** Base template rollup when personalized hours/cost differ (Story 2.10). */
  estimateComparison?: {
    baseHours: number
    baseCostMin?: number | null
    baseCostMax?: number | null
  }
  /** Plan-level actions (Story 2.13 — not on FAB) */
  onShareProject?: () => void
  onSaveMyPlan?: () => void
}

export function ProjectGuideHeader({
  title,
  difficulty,
  estimatedHours,
  estimatedCostMin,
  estimatedCostMax,
  tools = [],
  materials = [],
  isCustomized = false,
  customizationSummary,
  answerChipLabels = [],
  onEditCustomization,
  answersFingerprint,
  anonymousRefinement,
  estimateComparison,
  onShareProject,
  onSaveMyPlan,
}: ProjectGuideHeaderProps) {
  const { ref: estimatesGroupRef, entry: estimatesEntry } = useIntersection<HTMLDivElement>({
    threshold: 0,
  })
  const isEstimatesInView = estimatesEntry?.isIntersecting ?? true
  const labelFingerprint = answerChipLabels.join('\0')
  const [toolsExpanded, setToolsExpanded] = useState(false)
  const difficultyColor =
    (diyDifficultyBadgeColor as Record<string, string>)[difficulty] ?? 'gray'
  const difficultyLevel = DIFFICULTY_ICONS[difficulty] ?? 1

  const hasToolsOrMaterials = tools.length > 0 || materials.length > 0
  const hasCostEstimate = estimatedCostMin != null || estimatedCostMax != null

  // Track previous values to detect changes and show sparkles
  const prevHoursRef = useRef<number | undefined>(undefined)
  const prevCostMinRef = useRef<number | undefined>(undefined)
  const prevCostMaxRef = useRef<number | undefined>(undefined)
  const prevToolCountRef = useRef<number | undefined>(undefined)
  const prevMaterialCountRef = useRef<number | undefined>(undefined)

  const [showHoursSparkle, setShowHoursSparkle] = useState(false)
  const [showCostSparkle, setShowCostSparkle] = useState(false)
  const [showToolsSparkle, setShowToolsSparkle] = useState(false)
  const [showMaterialsSparkle, setShowMaterialsSparkle] = useState(false)

  const prefersReducedMotion = useReducedMotion(false, { getInitialValueInEffect: false })
  const pendingSparkleTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  useEffect(() => {
    if (answersFingerprint === undefined) return
    for (const id of pendingSparkleTimeoutsRef.current) {
      clearTimeout(id)
    }
    pendingSparkleTimeoutsRef.current = []
    setShowHoursSparkle(false)
    setShowCostSparkle(false)
    setShowToolsSparkle(false)
    setShowMaterialsSparkle(false)
  }, [answersFingerprint])

  const scheduleHeaderSparkle = useCallback(
    (setter: (v: boolean) => void, priority: 1 | 2 | 3) => {
      const delayMs = prefersReducedMotion ? 0 : getAnimatedValueTransitionEndMs(priority)
      const id = setTimeout(() => {
        setter(true)
        pendingSparkleTimeoutsRef.current = pendingSparkleTimeoutsRef.current.filter(t => t !== id)
      }, delayMs)
      pendingSparkleTimeoutsRef.current.push(id)
      return id
    },
    [prefersReducedMotion]
  )

  // Detect value changes; show sparkles after AnimatedValue transition completes (Epic Story 2.7).
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (prevHoursRef.current !== undefined && prevHoursRef.current !== estimatedHours) {
      timeoutId = scheduleHeaderSparkle(setShowHoursSparkle, 1)
    }
    prevHoursRef.current = estimatedHours
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        pendingSparkleTimeoutsRef.current = pendingSparkleTimeoutsRef.current.filter(t => t !== timeoutId)
      }
    }
  }, [estimatedHours, scheduleHeaderSparkle])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const minChanged =
      prevCostMinRef.current !== undefined && prevCostMinRef.current !== estimatedCostMin
    const maxChanged =
      prevCostMaxRef.current !== undefined && prevCostMaxRef.current !== estimatedCostMax
    if (minChanged || maxChanged) {
      timeoutId = scheduleHeaderSparkle(setShowCostSparkle, 1)
    }
    prevCostMinRef.current = estimatedCostMin
    prevCostMaxRef.current = estimatedCostMax
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        pendingSparkleTimeoutsRef.current = pendingSparkleTimeoutsRef.current.filter(t => t !== timeoutId)
      }
    }
  }, [estimatedCostMin, estimatedCostMax, scheduleHeaderSparkle])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (prevToolCountRef.current !== undefined && prevToolCountRef.current !== tools.length) {
      timeoutId = scheduleHeaderSparkle(setShowToolsSparkle, 3)
    }
    prevToolCountRef.current = tools.length
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        pendingSparkleTimeoutsRef.current = pendingSparkleTimeoutsRef.current.filter(t => t !== timeoutId)
      }
    }
  }, [tools.length, scheduleHeaderSparkle])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (
      prevMaterialCountRef.current !== undefined &&
      prevMaterialCountRef.current !== materials.length
    ) {
      timeoutId = scheduleHeaderSparkle(setShowMaterialsSparkle, 3)
    }
    prevMaterialCountRef.current = materials.length
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        pendingSparkleTimeoutsRef.current = pendingSparkleTimeoutsRef.current.filter(t => t !== timeoutId)
      }
    }
  }, [materials.length, scheduleHeaderSparkle])

  return (
    <Paper p="lg" withBorder radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
          <Title order={1} size="h2" style={{ flex: 1, minWidth: 0 }}>
            {title}
          </Title>
          {(onShareProject || onSaveMyPlan) && (
            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
              {onShareProject && (
                <ActionIcon
                  type="button"
                  variant="default"
                  size="lg"
                  radius="md"
                  onClick={onShareProject}
                  aria-label="Share project"
                >
                  <IconShare size={18} />
                </ActionIcon>
              )}
              {onSaveMyPlan && (
                <Button
                  type="button"
                  size="compact-sm"
                  leftSection={<IconBookmark size={16} />}
                  onClick={onSaveMyPlan}
                >
                  Save my plan
                </Button>
              )}
            </Group>
          )}
        </Group>

        {/* Quick stats: time + cost observed for scroll-past (Story 2.9); difficulty stays beside */}
        <Group gap="lg" wrap="wrap" align="flex-start">
          <Stack gap="xs">
            <Group ref={estimatesGroupRef} gap="lg" wrap="wrap" data-testid="estimates-header-row">
              {estimatedHours != null && (
                <Group gap="xs">
                  <IconClock size={18} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">
                    <AnimatedValue
                      value={estimatedHours}
                      suffix={estimatedHours === 1 ? ' hr active work' : ' hrs active work'}
                      fw={600}
                      priority={1}
                    />
                  </Text>
                  <SparkleIndicator
                    visible={showHoursSparkle}
                    persistent
                    ariaLabel="Time estimate updated"
                  />
                </Group>
              )}

              {hasCostEstimate && (
                <Group gap="xs">
                  <IconCurrencyDollar size={18} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">
                    <AnimatedValue
                      value={`$${estimatedCostMin ?? 0} - $${estimatedCostMax ?? estimatedCostMin ?? 0}`}
                      fw={600}
                      priority={1}
                    />
                  </Text>
                  <SparkleIndicator
                    visible={showCostSparkle}
                    persistent
                    ariaLabel="Cost estimate updated"
                  />
                </Group>
              )}
            </Group>

            {estimateComparison != null && (
              <EstimateComparison
                visible
                baseHours={estimateComparison.baseHours}
                baseCostMin={estimateComparison.baseCostMin}
                baseCostMax={estimateComparison.baseCostMax}
                isEstimatesInView={isEstimatesInView}
              />
            )}
          </Stack>

          <Group gap="xs">
            <IconBolt size={18} color={`var(--mantine-color-${difficultyColor}-6)`} />
            <Badge variant="light" color={difficultyColor} size="sm">
              {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
            </Badge>
            <Group gap={2}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Box
                  key={i}
                  w={6}
                  h={6}
                  style={{
                    borderRadius: '50%',
                    backgroundColor:
                      i < difficultyLevel
                        ? `var(--mantine-color-${difficultyColor}-6)`
                        : 'var(--mantine-color-gray-3)',
                  }}
                />
              ))}
            </Group>
          </Group>
        </Group>

        {/* Tools & Materials Summary (clickable to expand) */}
        {hasToolsOrMaterials && (
          <>
            <UnstyledButton
              type="button"
              onClick={() => setToolsExpanded(!toolsExpanded)}
              w="100%"
              aria-expanded={toolsExpanded}
              aria-controls="project-guide-tools-materials-panel"
            >
              <Group justify="space-between">
                <Group gap="lg" wrap="wrap">
                  {tools.length > 0 && (
                    <Group gap="xs">
                      <IconTool size={18} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">
                        <AnimatedValue
                          value={tools.length}
                          suffix={tools.length === 1 ? ' tool' : ' tools'}
                          fw={600}
                          priority={3}
                        />
                      </Text>
                      <SparkleIndicator
                        visible={showToolsSparkle}
                        persistent
                        ariaLabel="Tools list updated"
                      />
                    </Group>
                  )}

                  {materials.length > 0 && (
                    <Group gap="xs">
                      <IconPackage size={18} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">
                        <AnimatedValue
                          value={materials.length}
                          suffix={materials.length === 1 ? ' material' : ' materials'}
                          fw={600}
                          priority={3}
                        />
                      </Text>
                      <SparkleIndicator
                        visible={showMaterialsSparkle}
                        persistent
                        ariaLabel="Materials list updated"
                      />
                    </Group>
                  )}
                </Group>

                {toolsExpanded ? (
                  <IconChevronUp size={16} color="var(--mantine-color-dimmed)" />
                ) : (
                  <IconChevronDown size={16} color="var(--mantine-color-dimmed)" />
                )}
              </Group>
            </UnstyledButton>

            <Collapse in={toolsExpanded}>
              <Stack id="project-guide-tools-materials-panel" gap="md" pt="xs">
                {tools.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">
                      Tools ({tools.length})
                    </Text>
                    <Group gap="xs" wrap="wrap">
                      {tools.map((tool) => (
                        <Badge key={tool.name} variant="light" color="orange" size="sm">
                          {tool.name}
                        </Badge>
                      ))}
                    </Group>
                  </Box>
                )}

                {tools.length > 0 && materials.length > 0 && <Divider />}

                {materials.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">
                      Materials ({materials.length})
                    </Text>
                    <Stack gap="xs">
                      {materials.map((material) => (
                        <Group key={material.name} justify="space-between">
                          <Text size="sm">
                            {material.name}
                            {material.quantity && material.unit && (
                              <Text span c="dimmed" size="xs" ml={4}>
                                ({material.quantity} {material.unit})
                              </Text>
                            )}
                          </Text>
                          {material.cost && (
                            <Text size="sm" c="dimmed">
                              ~${material.cost}
                            </Text>
                          )}
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}

                {hasCostEstimate && (
                  <>
                    <Divider />
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>
                        Estimated Total
                      </Text>
                      <Text size="sm" fw={600} c="green">
                        ${estimatedCostMin ?? 0} - ${estimatedCostMax ?? estimatedCostMin ?? 0}
                      </Text>
                    </Group>
                  </>
                )}
              </Stack>
            </Collapse>
          </>
        )}

        {anonymousRefinement?.show && (
          <Box mt="xs">
            <AnonymousRefinementBar
              count={anonymousRefinement.count}
              display={anonymousRefinement.display}
              accountMode={anonymousRefinement.accountMode}
              onSaveProgress={anonymousRefinement.onSaveProgress}
              onSignUp={anonymousRefinement.onSignUp}
              onSignIn={anonymousRefinement.onSignIn}
            />
          </Box>
        )}

        {/* Personalization: badge + chips (Story 2.9) or legacy line when no chip labels */}
        {isCustomized && answerChipLabels.length > 0 ? (
          <PersonalizedBadge
            answerChipLabels={answerChipLabels}
            isEstimatesInView={isEstimatesInView}
            onEditCustomization={() => onEditCustomization?.()}
            labelFingerprint={labelFingerprint}
          />
        ) : isCustomized && customizationSummary ? (
          <UnstyledButton
            onClick={onEditCustomization}
            aria-label={`Edit plan customization. Summary: ${PERSONALIZED_FOR_YOUR} ${customizationSummary.values.join(', ')}.`}
            type="button"
          >
            <Group gap="xs">
              <IconSparkles size={16} color="var(--mantine-color-primary-6)" aria-hidden />
              <Text size="sm" c="primary" fw={500}>
                {PERSONALIZED_FOR_YOUR}: {customizationSummary.values.join(', ')}
              </Text>
              <Text size="sm" c="dimmed">
                [Edit]
              </Text>
            </Group>
          </UnstyledButton>
        ) : (
          <UnstyledButton
            onClick={onEditCustomization}
            aria-label="Open customization to tailor this project guide to your situation"
            type="button"
          >
            <Group gap="xs">
              <IconSparkles size={16} color="var(--mantine-color-dimmed)" aria-hidden />
              <Text size="sm" c="dimmed">
                Tap ⚓ to customize for your situation
              </Text>
            </Group>
          </UnstyledButton>
        )}
      </Stack>
    </Paper>
  )
}
